/**
 * OfferingEditorDialog — Phase A1b
 *
 * Create/edit a ServicePackage. Bilingual name + description, refund-
 * policy mode with inline Article-19 warning when mode='none' is chosen
 * for SPA-sold packs, transferability default 'household', e-invoice
 * required toggle, and a TierLadderEditor child that enforces
 * strictly-ascending paid_sessions client-side AND shows the live
 * tier-badge preview ("5 / 10+1 / 20+3") at the top.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, Loader2, Plus, Trash2 } from 'lucide-react'

import {
  billing,
  type ServicePackage,
  type ServicePackageCreatePayload,
  type ServicePackageTierPayload,
} from '../api/client'
import { Button } from '../components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'

interface EditorProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  existing?: ServicePackage | null
  onSaved: () => void
}

type Draft = {
  service_type: 'boarding' | 'daycare' | 'grooming'
  name_en: string
  name_zh: string
  description_en: string
  description_zh: string
  base_price_minor: number
  validity_days: number | ''
  transferability: 'household' | 'owner_only'
  refund_policy_mode: 'none' | 'prorated' | 'prorated_with_fee' | 'full_within_window'
  refund_window_days: number | ''
  refund_early_termination_fee_minor: number
  einvoice_required: boolean
  tiers: ServicePackageTierPayload[]
}

function emptyDraft(): Draft {
  return {
    service_type: 'grooming',
    name_en: '',
    name_zh: '',
    description_en: '',
    description_zh: '',
    base_price_minor: 0,
    validity_days: 180,
    transferability: 'household',
    refund_policy_mode: 'prorated',
    refund_window_days: '',
    refund_early_termination_fee_minor: 0,
    einvoice_required: false,
    tiers: [
      { paid_sessions: 5, bonus_sessions: 0, price_minor: 0, is_default: true },
    ],
  }
}

function fromExisting(p: ServicePackage): Draft {
  return {
    service_type: p.service_type,
    name_en: p.name_en,
    name_zh: p.name_zh,
    description_en: p.description_en ?? '',
    description_zh: p.description_zh ?? '',
    base_price_minor: p.base_price_minor,
    validity_days: p.validity_days ?? '',
    transferability: p.transferability,
    refund_policy_mode: p.refund_policy_mode,
    refund_window_days: p.refund_window_days ?? '',
    refund_early_termination_fee_minor: p.refund_early_termination_fee_minor,
    einvoice_required: p.einvoice_required,
    tiers: p.tiers.map((t) => ({
      paid_sessions: t.paid_sessions,
      bonus_sessions: t.bonus_sessions,
      price_minor: t.price_minor,
      label_en: t.label_en,
      label_zh: t.label_zh,
      is_default: t.is_default,
      display_order: t.display_order,
    })),
  }
}

function validateClient(draft: Draft): string | null {
  if (!draft.name_en.trim() || !draft.name_zh.trim()) return 'name_required'
  if (draft.base_price_minor < 0) return 'price_negative'
  if (draft.tiers.length === 0) return 'tiers_required'
  let last = 0
  for (const t of draft.tiers) {
    if (t.paid_sessions < 1) return 'tier_paid_min'
    if (t.paid_sessions <= last) return 'tier_ascending'
    if (t.price_minor < 0) return 'tier_price_negative'
    last = t.paid_sessions
  }
  return null
}

export function OfferingEditorDialog({
  open,
  onOpenChange,
  existing,
  onSaved,
}: EditorProps) {
  const { t } = useTranslation()
  const [draft, setDraft] = useState<Draft>(emptyDraft)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setDraft(existing ? fromExisting(existing) : emptyDraft())
      setError(null)
    }
  }, [open, existing])

  const update = <K extends keyof Draft>(k: K, v: Draft[K]) => {
    setDraft((d) => ({ ...d, [k]: v }))
  }

  const tierBadgePreview = draft.tiers
    .map((t) => (t.bonus_sessions ? `${t.paid_sessions}+${t.bonus_sessions}` : `${t.paid_sessions}`))
    .join(' / ')

  async function save() {
    const clientErr = validateClient(draft)
    if (clientErr) {
      setError(clientErr)
      return
    }
    setSaving(true)
    setError(null)
    try {
      const payload: ServicePackageCreatePayload = {
        service_type: draft.service_type,
        name_en: draft.name_en.trim(),
        name_zh: draft.name_zh.trim(),
        description_en: draft.description_en.trim() || null,
        description_zh: draft.description_zh.trim() || null,
        base_price_minor: draft.base_price_minor,
        currency: existing?.currency ?? 'TWD',
        validity_days:
          draft.validity_days === '' ? null : Number(draft.validity_days),
        transferability: draft.transferability,
        refund_policy_mode: draft.refund_policy_mode,
        refund_window_days:
          draft.refund_window_days === '' ? null : Number(draft.refund_window_days),
        refund_early_termination_fee_minor: draft.refund_early_termination_fee_minor,
        einvoice_required: draft.einvoice_required,
        tiers: draft.tiers,
      }
      if (existing) {
        await billing.updatePackage(existing.id, payload)
      } else {
        await billing.createPackage(payload)
      }
      onSaved()
      onOpenChange(false)
    } catch (e) {
      const msg =
        (e as { detail?: string; message?: string })?.detail ??
        (e as { message?: string })?.message ??
        'unknown_error'
      setError(msg)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {existing ? t('packages.editor.titleEdit') : t('packages.editor.titleCreate')}
          </DialogTitle>
          <DialogDescription>
            {t('packages.editor.subtitle')}
          </DialogDescription>
        </DialogHeader>

        {/* Live tier-badge preview at the top — design-spec'd */}
        <div className="rounded border bg-muted/30 px-3 py-2 text-sm">
          <span className="text-muted-foreground">{t('packages.editor.tierPreview')}</span>
          <span className="ml-2 font-mono font-medium" data-testid="tier-preview">
            {tierBadgePreview || '—'}
          </span>
        </div>

        <div className="space-y-4">
          {/* Service type — locked when editing */}
          <div>
            <Label htmlFor="service-type">{t('packages.editor.serviceType')}</Label>
            <select
              id="service-type"
              value={draft.service_type}
              onChange={(e) =>
                update('service_type', e.target.value as Draft['service_type'])
              }
              disabled={!!existing}
              className="mt-1 h-10 w-full rounded border bg-background px-3 text-sm disabled:opacity-50"
              data-testid="editor-service-type"
            >
              <option value="grooming">{t('packages.serviceType.grooming')}</option>
              <option value="boarding">{t('packages.serviceType.boarding')}</option>
              <option value="daycare">{t('packages.serviceType.daycare')}</option>
            </select>
            {existing && (
              <p className="mt-1 text-xs text-muted-foreground">
                {t('packages.editor.serviceTypeLocked')}
              </p>
            )}
          </div>

          {/* Bilingual name */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="name-en">{t('packages.editor.nameEn')}</Label>
              <Input
                id="name-en"
                value={draft.name_en}
                onChange={(e) => update('name_en', e.target.value)}
                placeholder="5-Groom Pack"
                data-testid="editor-name-en"
              />
            </div>
            <div>
              <Label htmlFor="name-zh">{t('packages.editor.nameZh')}</Label>
              <Input
                id="name-zh"
                value={draft.name_zh}
                onChange={(e) => update('name_zh', e.target.value)}
                placeholder="美容五次組"
                data-testid="editor-name-zh"
              />
            </div>
            <div>
              <Label htmlFor="desc-en">{t('packages.editor.descEn')}</Label>
              <Input
                id="desc-en"
                value={draft.description_en}
                onChange={(e) => update('description_en', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="desc-zh">{t('packages.editor.descZh')}</Label>
              <Input
                id="desc-zh"
                value={draft.description_zh}
                onChange={(e) => update('description_zh', e.target.value)}
              />
            </div>
          </div>

          {/* Base price + validity */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="base-price">{t('packages.editor.basePriceMinor')}</Label>
              <Input
                id="base-price"
                type="number"
                min={0}
                value={draft.base_price_minor}
                onChange={(e) =>
                  update('base_price_minor', Number(e.target.value) || 0)
                }
                data-testid="editor-base-price"
              />
              <p className="mt-1 text-xs text-muted-foreground">
                {t('packages.editor.basePriceHint')}
              </p>
            </div>
            <div>
              <Label htmlFor="validity-days">{t('packages.editor.validityDays')}</Label>
              <Input
                id="validity-days"
                type="number"
                min={1}
                value={draft.validity_days}
                onChange={(e) =>
                  update(
                    'validity_days',
                    e.target.value === '' ? '' : Number(e.target.value),
                  )
                }
                placeholder="180"
              />
            </div>
          </div>

          {/* Transferability + e-invoice */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="transferability">{t('packages.editor.transferability')}</Label>
              <select
                id="transferability"
                value={draft.transferability}
                onChange={(e) =>
                  update('transferability', e.target.value as Draft['transferability'])
                }
                className="mt-1 h-10 w-full rounded border bg-background px-3 text-sm"
              >
                <option value="household">{t('packages.transferability.household')}</option>
                <option value="owner_only">{t('packages.transferability.owner_only')}</option>
              </select>
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={draft.einvoice_required}
                  onChange={(e) => update('einvoice_required', e.target.checked)}
                />
                {t('packages.editor.einvoiceRequired')}
              </label>
            </div>
          </div>

          {/* Refund policy */}
          <div>
            <Label htmlFor="refund-mode">{t('packages.editor.refundMode')}</Label>
            <select
              id="refund-mode"
              value={draft.refund_policy_mode}
              onChange={(e) =>
                update(
                  'refund_policy_mode',
                  e.target.value as Draft['refund_policy_mode'],
                )
              }
              className="mt-1 h-10 w-full rounded border bg-background px-3 text-sm"
              data-testid="editor-refund-mode"
            >
              <option value="prorated">{t('packages.refundPolicy.prorated')}</option>
              <option value="prorated_with_fee">{t('packages.refundPolicy.prorated_with_fee')}</option>
              <option value="full_within_window">{t('packages.refundPolicy.full_within_window')}</option>
              <option value="none">{t('packages.refundPolicy.none')}</option>
            </select>
            {draft.refund_policy_mode === 'none' && (
              <div
                className="mt-2 rounded border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900"
                data-testid="article-19-warning"
              >
                {t('packages.editor.article19Warning')}
              </div>
            )}
            {draft.refund_policy_mode === 'full_within_window' && (
              <div className="mt-2">
                <Label htmlFor="refund-window-days">
                  {t('packages.editor.refundWindowDays')}
                </Label>
                <Input
                  id="refund-window-days"
                  type="number"
                  min={0}
                  value={draft.refund_window_days}
                  onChange={(e) =>
                    update(
                      'refund_window_days',
                      e.target.value === '' ? '' : Number(e.target.value),
                    )
                  }
                  placeholder="7"
                />
              </div>
            )}
            {draft.refund_policy_mode === 'prorated_with_fee' && (
              <div className="mt-2">
                <Label htmlFor="refund-fee">
                  {t('packages.editor.refundFeeMinor')}
                </Label>
                <Input
                  id="refund-fee"
                  type="number"
                  min={0}
                  value={draft.refund_early_termination_fee_minor}
                  onChange={(e) =>
                    update(
                      'refund_early_termination_fee_minor',
                      Number(e.target.value) || 0,
                    )
                  }
                />
              </div>
            )}
          </div>

          {/* Tier ladder editor */}
          <TierLadderEditor
            tiers={draft.tiers}
            onChange={(tiers) => update('tiers', tiers)}
          />

          {/* B2a — price-change impact banner (only when editing existing). */}
          {existing && (
            <PriceChangeImpactBanner
              existing={existing}
              draftTiers={draft.tiers}
            />
          )}
        </div>

        {error && (
          <p className="text-sm text-destructive" data-testid="editor-error">
            {t(`packages.editor.errors.${error}`, error)}
          </p>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>
            {t('packages.editor.cancel')}
          </Button>
          <Button onClick={save} disabled={saving} data-testid="editor-save">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {existing
              ? t('packages.editor.saveExisting')
              : t('packages.editor.saveNew')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

interface TierLadderProps {
  tiers: ServicePackageTierPayload[]
  onChange: (next: ServicePackageTierPayload[]) => void
}

function TierLadderEditor({ tiers, onChange }: TierLadderProps) {
  const { t } = useTranslation()

  function setRow(i: number, patch: Partial<ServicePackageTierPayload>) {
    const next = tiers.map((row, idx) => (idx === i ? { ...row, ...patch } : row))
    onChange(next)
  }

  function setDefault(i: number) {
    const next = tiers.map((row, idx) => ({ ...row, is_default: idx === i }))
    onChange(next)
  }

  function addRow() {
    const last = tiers[tiers.length - 1]?.paid_sessions ?? 0
    onChange([
      ...tiers,
      {
        paid_sessions: last + 5,
        bonus_sessions: 0,
        price_minor: 0,
        is_default: tiers.length === 0,
      },
    ])
  }

  function removeRow(i: number) {
    const next = tiers.filter((_, idx) => idx !== i)
    if (next.length > 0 && !next.some((r) => r.is_default)) {
      next[0] = { ...next[0], is_default: true }
    }
    onChange(next)
  }

  return (
    <div>
      <Label>{t('packages.editor.tierLadder')}</Label>
      <p className="mb-2 text-xs text-muted-foreground">
        {t('packages.editor.tierLadderHint')}
      </p>
      <div className="space-y-2">
        {tiers.map((row, i) => {
          const total = row.paid_sessions + (row.bonus_sessions ?? 0)
          const unitCost =
            row.paid_sessions > 0 ? Math.round(row.price_minor / row.paid_sessions / 100) : 0
          return (
            <div
              key={i}
              className="grid grid-cols-12 items-center gap-2 rounded border p-2"
              data-testid={`tier-row-${i}`}
            >
              <div className="col-span-1 flex justify-center">
                <input
                  type="radio"
                  name="default-tier"
                  checked={row.is_default ?? false}
                  onChange={() => setDefault(i)}
                  aria-label={t('packages.editor.defaultTier')}
                />
              </div>
              <div className="col-span-3 sm:col-span-2">
                <Label className="text-xs">{t('packages.editor.paidSessions')}</Label>
                <Input
                  type="number"
                  min={1}
                  value={row.paid_sessions}
                  onChange={(e) =>
                    setRow(i, { paid_sessions: Number(e.target.value) || 0 })
                  }
                  className="h-8"
                />
              </div>
              <div className="col-span-3 sm:col-span-2">
                <Label className="text-xs">{t('packages.editor.bonusSessions')}</Label>
                <Input
                  type="number"
                  min={0}
                  value={row.bonus_sessions ?? 0}
                  onChange={(e) =>
                    setRow(i, { bonus_sessions: Number(e.target.value) || 0 })
                  }
                  className="h-8"
                />
              </div>
              <div className="col-span-4 sm:col-span-3">
                <Label className="text-xs">{t('packages.editor.priceMinor')}</Label>
                <Input
                  type="number"
                  min={0}
                  value={row.price_minor}
                  onChange={(e) =>
                    setRow(i, { price_minor: Number(e.target.value) || 0 })
                  }
                  className="h-8"
                />
                {row.paid_sessions > 0 && row.price_minor > 0 && (
                  <p className="mt-0.5 text-[10px] text-muted-foreground">
                    {t('packages.editor.unitCost', { unit: unitCost, total })}
                  </p>
                )}
              </div>
              <div className="col-span-1 flex justify-end">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeRow(i)}
                  disabled={tiers.length <= 1}
                  aria-label={t('packages.editor.removeTier')}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )
        })}
      </div>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={addRow}
        className="mt-2"
        data-testid="editor-add-tier"
      >
        <Plus className="mr-1 h-4 w-4" />
        {t('packages.editor.addTier')}
      </Button>
    </div>
  )
}

interface PriceChangeImpactBannerProps {
  existing: ServicePackage
  draftTiers: ServicePackageTierPayload[]
}

/**
 * B2a — fetches per-tier impact data from
 * GET /merchant/me/packages/{pkg}/tiers/{tier}/price-change-impact for
 * every existing tier whose price has been edited. Debounced 600ms so
 * typing in a price field doesn't hammer the endpoint. Old-price
 * honoring is automatic via snapshot pattern; this banner exists to
 * surface blast radius before the merchant saves.
 */
function PriceChangeImpactBanner({
  existing,
  draftTiers,
}: PriceChangeImpactBannerProps) {
  const { t } = useTranslation()
  const [impacts, setImpacts] = useState<
    Array<{
      tier_id: string
      active: number
      pending: number
      daysSinceLastSale: number | null
    }>
  >([])
  const [loading, setLoading] = useState(false)
  const debounceRef = useRef<number | null>(null)

  // Match draft tiers to existing tiers by paid_sessions (the natural key).
  const changedTierIds = useMemo(() => {
    const ids: string[] = []
    for (const draft of draftTiers) {
      const orig = existing.tiers.find(
        (e) => e.paid_sessions === draft.paid_sessions,
      )
      if (orig && orig.price_minor !== draft.price_minor) {
        ids.push(orig.id)
      }
    }
    return ids
  }, [existing.tiers, draftTiers])

  useEffect(() => {
    if (changedTierIds.length === 0) {
      setImpacts([])
      return
    }
    if (debounceRef.current) {
      window.clearTimeout(debounceRef.current)
    }
    debounceRef.current = window.setTimeout(async () => {
      setLoading(true)
      try {
        const results = await Promise.all(
          changedTierIds.map((tid) =>
            billing
              .getTierPriceChangeImpact(existing.id, tid)
              .then((r) => ({
                tier_id: r.tier_id,
                active: r.active_purchases_on_old_price,
                pending: r.pending_checkout_count,
                daysSinceLastSale: r.days_since_last_sale,
              }))
              .catch(() => null),
          ),
        )
        setImpacts(results.filter((x): x is NonNullable<typeof x> => !!x))
      } finally {
        setLoading(false)
      }
    }, 600)
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current)
    }
  }, [changedTierIds, existing.id])

  if (changedTierIds.length === 0) return null

  const totalActive = impacts.reduce((s, i) => s + i.active, 0)
  const totalPending = impacts.reduce((s, i) => s + i.pending, 0)
  const lastSaleDays = impacts.reduce<number | null>(
    (min, i) =>
      i.daysSinceLastSale !== null &&
      (min === null || i.daysSinceLastSale < min)
        ? i.daysSinceLastSale
        : min,
    null,
  )

  return (
    <div
      className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
      data-testid="price-change-impact-banner"
    >
      <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
      <div className="flex-1">
        <div className="font-medium">{t('packages.priceChange.impactTitle')}</div>
        <ul className="mt-1 list-disc space-y-0.5 pl-4 text-xs">
          {loading && (
            <li className="list-none">
              <Loader2 className="inline h-3 w-3 animate-spin" />
            </li>
          )}
          {!loading && (
            <>
              <li>
                {t('packages.priceChange.activeOnOldPrice', {
                  count: totalActive,
                })}
              </li>
              {totalPending > 0 && (
                <li>
                  {t('packages.priceChange.pendingCheckouts', {
                    count: totalPending,
                  })}
                </li>
              )}
              <li>
                {lastSaleDays === null
                  ? t('packages.priceChange.noSales')
                  : t('packages.priceChange.lastSale', { days: lastSaleDays })}
              </li>
              <li className="list-none italic text-amber-700">
                {t('packages.priceChange.oldPriceHonored')}
              </li>
            </>
          )}
        </ul>
      </div>
    </div>
  )
}
